import {
  compileDesignBoundApplication,
  type DesignBoundApplicationArtifact,
  type DesignBoundApplicationInput,
  type DesignOntologyReleaseResolver,
} from '@cogo/agentic-generator/design-ontology-binding'
import type {
  LibraryRagOrchestrationEvidence,
  LibraryRagOrchestrationResult,
} from '@cogo/agentic-generator/library-rag-agent-orchestration'
import {
  normalizeStudioDesignSystemRelease,
  type StudioDesignOntologyBindingEvidence,
  type StudioProjectPlan,
} from '@/features/project/lib/studio-project-plan'
import type { AppSpecFull } from '@/services/block-api-service'
import { validateStudioLibraryRagSuccessReceipt } from './studio-library-rag-receipt'

export type StudioLibraryDesignOntologyBindingErrorCode =
  | 'studio_library_prompt_mismatch'
  | 'studio_design_ontology_binding_rejected'
  | 'studio_design_system_release_missing'
  | 'studio_design_system_release_mismatch'
  | 'studio_design_system_web_target_missing'

export class StudioLibraryDesignOntologyBindingError extends Error {
  override readonly name = 'StudioLibraryDesignOntologyBindingError'

  constructor(
    readonly code: StudioLibraryDesignOntologyBindingErrorCode,
    readonly gapCodes: readonly string[] = [],
  ) {
    super([code, ...gapCodes].join(':'))
  }
}

export interface StudioLibraryDesignOntologyBindingInput {
  application: DesignBoundApplicationInput
  resolver: DesignOntologyReleaseResolver
}

const COMPETING_PRESENTATION_FIELDS = [
  'themeHint',
  'designSystemTokens',
  'designSystemContract',
  'designContract',
  'templateEnforcement',
  'templateGenerationModelPolicy',
  'goldenRecipePack',
] as const

function rejectCompetingPresentationAuthority(appSpec: AppSpecFull): void {
  const record = appSpec as unknown as Record<string, unknown>
  const competingField = COMPETING_PRESENTATION_FIELDS.find(
    (field) => record[field] !== undefined && record[field] !== null,
  )
  if (competingField) {
    throw new StudioLibraryDesignOntologyBindingError(
      'studio_design_ontology_binding_rejected',
      [`studio-library-competing-presentation:${competingField}`],
    )
  }
}

/**
 * Projects the exact compiled Library runtime and Design Ontology render
 * contract into the only AppSpec shape allowed for the Library entry point.
 * Live planning supplies non-presentation content and admission evidence only;
 * none of its page, block, state, datasource, action, token, or renderer
 * structure crosses this boundary.
 */
export function projectStudioLibraryDesignBoundAppSpec(input: {
  appSpec: AppSpecFull
  projectPlan: StudioProjectPlan
  artifact: DesignBoundApplicationArtifact
  libraryRagOrchestrationEvidence?: LibraryRagOrchestrationEvidence
}): AppSpecFull {
  rejectCompetingPresentationAuthority(input.appSpec)

  const artifact = structuredClone(input.artifact)
  const libraryRagOrchestrationEvidence =
    input.libraryRagOrchestrationEvidence === undefined
      ? undefined
      : structuredClone(input.libraryRagOrchestrationEvidence)
  const runtime = artifact.library.runtime
  const runtimeBlockIds = runtime.blocks.map((block) => block.blockId)
  const runtimeSemanticNodeIds = runtime.blocks.map(
    (block) => block.semanticNodeId,
  )
  const designNodeIds = artifact.design.nodes.map(
    (node) => node.nodeId,
  )
  const runtimeSemanticNodeIdSet = new Set(runtimeSemanticNodeIds)
  const designNodeIdSet = new Set(designNodeIds)
  const designNodesById = new Map(
    artifact.design.nodes.map((node) => [node.nodeId, node]),
  )
  // The block↔design-node relation is MANY-TO-ONE, not bijective: a UX instance
  // that renders on two journey steps produces one block per (page, node), so
  // the compiler legitimately emits more blocks than design nodes. What the
  // projection actually depends on is
  //   (1) SET EQUALITY — no block renders a design node the compiled design
  //       does not declare, and no compiled design node goes unrendered, and
  //   (2) blockId UNIQUENESS — AppSpec blocks are keyed by `blockId` and
  //       `pages[].blocks` references them by that name, so a repeated blockId
  //       silently collapses two occurrences into one.
  // Cardinality beyond that is the application's business.
  if (
    new Set(runtimeBlockIds).size !== runtimeBlockIds.length ||
    designNodeIdSet.size !== designNodeIds.length ||
    runtimeSemanticNodeIdSet.size !== designNodeIdSet.size ||
    [...runtimeSemanticNodeIdSet].some(
      (nodeId) => !designNodeIdSet.has(nodeId),
    ) ||
    [...designNodeIdSet].some(
      (nodeId) => !runtimeSemanticNodeIdSet.has(nodeId),
    )
  ) {
    throw new StudioLibraryDesignOntologyBindingError(
      'studio_design_ontology_binding_rejected',
      ['studio-library-design-node-projection-mismatch'],
    )
  }

  return {
    appId: artifact.library.applicationInstanceId,
    name: input.appSpec.name,
    description: runtime.userGoal,
    appClass: input.projectPlan.appClass,
    targetPlatform: 'web',
    generationTargetPlatform: 'react',
    runtimeTargets: ['web'],
    projectPlan: input.projectPlan,
    adaptiveRuntime: input.projectPlan.adaptiveRuntime,
    planningSource: input.appSpec.planningSource,
    decomposePlanning: input.appSpec.decomposePlanning,
    ontologyRetrievalTrace: input.appSpec.ontologyRetrievalTrace,
    deterministic: true,
    pages: runtime.pages.map((page, index) => ({
      id: page.pageId,
      route:
        index === 0
          ? '/'
          : `/library/${index + 1}/${encodeURIComponent(page.pageId)}`,
      title: page.journeyStepId,
      blocks: runtime.blocks
        .filter((block) => block.pageId === page.pageId)
        .map((block) => block.blockId),
    })),
    blocks: runtime.blocks.map((block) => ({
      name: block.blockId,
      type: 'application-composition-node',
      description: designNodesById.get(block.semanticNodeId)!.nodeId,
      binds: [],
    })),
    datasources: runtime.datasources.map((datasource) => ({
      name: datasource.datasourceId,
      type: 'library-capability',
      schema: { capabilityRef: datasource.capabilityRef },
    })),
    globalState: Object.fromEntries(
      runtime.applicationStateIds.map((stateId) => [
        stateId,
        { type: 'unknown', default: null },
      ]),
    ),
    designBoundApplicationArtifact: artifact,
    ...(libraryRagOrchestrationEvidence
      ? {
          libraryRagOrchestrationEvidence,
        }
      : {}),
    applicationCompositionPlan: artifact.design,
  }
}

function normalizedLibraryProjectPlan(input: {
  projectPlan: StudioProjectPlan
  designSystemRelease: NonNullable<StudioProjectPlan['designSystemRelease']>
  designOntologyBinding: StudioDesignOntologyBindingEvidence
}): StudioProjectPlan {
  const stylePresetId = input.projectPlan.stylePresetId?.trim()
  const templateId = input.projectPlan.templateId?.trim()
  const designSystemId = input.projectPlan.designSystemId?.trim()
  if (
    stylePresetId ||
    templateId ||
    (designSystemId &&
      designSystemId !== input.designSystemRelease.releaseId)
  ) {
    throw new StudioLibraryDesignOntologyBindingError(
      'studio_design_ontology_binding_rejected',
      ['studio-library-project-plan-presentation-authority'],
    )
  }
  if (
    input.projectPlan.runtimeTargets.length !== 1 ||
    input.projectPlan.runtimeTargets[0] !== 'web'
  ) {
    throw new StudioLibraryDesignOntologyBindingError(
      'studio_design_system_web_target_missing',
    )
  }

  return {
    intent: input.projectPlan.intent.trim(),
    ...(input.projectPlan.outputKind
      ? { outputKind: input.projectPlan.outputKind }
      : {}),
    appClass: input.projectPlan.appClass,
    runtimeTargets: ['web'],
    designSystemRelease: structuredClone(input.designSystemRelease),
    designOntologyBinding: structuredClone(input.designOntologyBinding),
    ...(input.projectPlan.dynamicIslands
      ? { dynamicIslands: [...input.projectPlan.dynamicIslands] }
      : {}),
    ...(input.projectPlan.securityProfile
      ? { securityProfile: input.projectPlan.securityProfile }
      : {}),
    ...(input.projectPlan.performanceBudget
      ? {
          performanceBudget: {
            ...(input.projectPlan.performanceBudget.firstPreviewP95Ms !==
            undefined
              ? {
                  firstPreviewP95Ms:
                    input.projectPlan.performanceBudget.firstPreviewP95Ms,
                }
              : {}),
            ...(input.projectPlan.performanceBudget.warmRetrievalP95Ms !==
            undefined
              ? {
                  warmRetrievalP95Ms:
                    input.projectPlan.performanceBudget.warmRetrievalP95Ms,
                }
              : {}),
            ...(input.projectPlan.performanceBudget.evidenceCompleteP95Ms !==
            undefined
              ? {
                  evidenceCompleteP95Ms:
                    input.projectPlan.performanceBudget.evidenceCompleteP95Ms,
                }
              : {}),
          },
        }
      : {}),
    ...(input.projectPlan.adaptiveRuntime
      ? {
          adaptiveRuntime: {
            schemaVersion: 'studio-adaptive-runtime-plan.v1',
            enabled: input.projectPlan.adaptiveRuntime.enabled,
            situationSignals: [
              ...input.projectPlan.adaptiveRuntime.situationSignals,
            ],
            adaptationAxes: [
              ...input.projectPlan.adaptiveRuntime.adaptationAxes,
            ],
            localStatePolicy: 'block-local-first',
            globalStatePolicy: 'appData-shared',
            evidencePolicy: {
              requireMeasuredDesignEvidence:
                input.projectPlan.adaptiveRuntime.evidencePolicy
                  .requireMeasuredDesignEvidence,
              quarantineUnmeasuredOntologyFacts:
                input.projectPlan.adaptiveRuntime.evidencePolicy
                  .quarantineUnmeasuredOntologyFacts,
              requireActionFlowTraceForDynamicApps:
                input.projectPlan.adaptiveRuntime.evidencePolicy
                  .requireActionFlowTraceForDynamicApps,
            },
            notes: [...input.projectPlan.adaptiveRuntime.notes],
          },
        }
      : {}),
  }
}

function bindCompiledArtifact(input: {
  projectPlan: StudioProjectPlan
  artifact: DesignBoundApplicationArtifact
}): {
  projectPlan: StudioProjectPlan
  artifact: DesignBoundApplicationArtifact
} {
  const designSystemRelease = normalizeStudioDesignSystemRelease(
    input.projectPlan.designSystemRelease,
  )
  if (!designSystemRelease) {
    throw new StudioLibraryDesignOntologyBindingError(
      'studio_design_system_release_missing',
    )
  }
  if (
    designSystemRelease.releaseId !== input.artifact.designOntologyReleaseRef
  ) {
    throw new StudioLibraryDesignOntologyBindingError(
      'studio_design_system_release_mismatch',
    )
  }
  if (!designSystemRelease.targetPlatforms.includes('web')) {
    throw new StudioLibraryDesignOntologyBindingError(
      'studio_design_system_web_target_missing',
    )
  }

  const designOntologyBinding: StudioDesignOntologyBindingEvidence = {
    schemaVersion: 'cogo.studio-design-ontology-binding-evidence.v1',
    designOntologyReleaseRef: input.artifact.designOntologyReleaseRef,
    applicationSemanticHash: input.artifact.applicationSemanticHash,
    designBindingHash: input.artifact.designBindingHash,
    renderContractHash: input.artifact.renderContractHash,
  }

  return {
    projectPlan: normalizedLibraryProjectPlan({
      projectPlan: input.projectPlan,
      designSystemRelease,
      designOntologyBinding,
    }),
    artifact: structuredClone(input.artifact),
  }
}

function rejectRagReceipt(gapCode: string): never {
  throw new StudioLibraryDesignOntologyBindingError(
    'studio_design_ontology_binding_rejected',
    [`studio-library-rag-receipt:${gapCode}`],
  )
}

export function bindStudioLibraryRagResolution(input: {
  projectPlan: StudioProjectPlan
  resolution: Extract<LibraryRagOrchestrationResult, { ok: true }>
}): {
  projectPlan: StudioProjectPlan
  artifact: DesignBoundApplicationArtifact
  evidence: LibraryRagOrchestrationEvidence
} {
  let resolution: unknown
  try {
    resolution = structuredClone(input.resolution)
  } catch {
    return rejectRagReceipt('receipt-invalid')
  }
  const validation = validateStudioLibraryRagSuccessReceipt(resolution)
  if (!validation.ok) {
    return rejectRagReceipt(validation.gapCode)
  }

  const { artifact, evidence } = validation.receipt

  return {
    ...bindCompiledArtifact({ projectPlan: input.projectPlan, artifact }),
    evidence: structuredClone(evidence),
  }
}

export async function bindStudioLibraryDesignOntology(input: {
  prompt: string
  projectPlan: StudioProjectPlan
  binding: StudioLibraryDesignOntologyBindingInput
}): Promise<{
  projectPlan: StudioProjectPlan
  artifact: DesignBoundApplicationArtifact
}> {
  if (
    input.binding.application.library.applicationBlueprint.userGoal !==
    input.prompt
  ) {
    throw new StudioLibraryDesignOntologyBindingError(
      'studio_library_prompt_mismatch',
    )
  }

  const compiled = await compileDesignBoundApplication(
    input.binding.application,
    input.binding.resolver,
  )
  if (!compiled.ok) {
    throw new StudioLibraryDesignOntologyBindingError(
      'studio_design_ontology_binding_rejected',
      [...new Set(compiled.gaps.map((gap) => gap.code))],
    )
  }

  return bindCompiledArtifact({
    projectPlan: input.projectPlan,
    artifact: compiled.artifact,
  })
}
